import dayjs from "dayjs";
import apiClient from "@/app/api/client";
import { endpoints } from "@/shared/api/endpoints";

export type BackendAuditLogItem = {
	id: string;
	orgId: string | null;
	userId: string | null;
	action: string;
	entityType: string;
	entityId: string;
	oldData: any | null;
	newData: any | null;
	ipAddress: string | null;
	createdAt: string;
	userName: string | null;
	orgName: string | null;
};

export type AuditLogListResponse = {
	success: true;
	data: {
		items: BackendAuditLogItem[];
		meta: {
			total: number;
			page: number;
			limit: number;
			totalPages: number;
		};
	};
};

export type AuditLogQueryParams = {
	action?: string;
	userId?: string;
	orgId?: string;
	entityType?: string;
	dateFrom?: string;
	dateTo?: string;
	page?: number;
	limit?: number;
};

export type AuditTone = "green" | "sky" | "red" | "amber";

export type AuditLog = {
	id: string;
	time: string;
	user: string;
	action: string;
	object: string;
	ip: string;
	tone: AuditTone;
	oldData: string[];
	newData: string[];
};

export const getAuditTone = (action: string): AuditTone => {
	const act = action.toLowerCase();
	if (
		act.includes("create") ||
		act.includes("add") ||
		act.includes("save") ||
		act.includes("post")
	) {
		return "green";
	}
	if (
		act.includes("delete") ||
		act.includes("remove") ||
		act.includes("cancel") ||
		act.includes("destroy")
	) {
		return "red";
	}
	if (
		act.includes("update") ||
		act.includes("edit") ||
		act.includes("change") ||
		act.includes("patch")
	) {
		return "amber";
	}
	return "sky";
};

export const formatDataForDiff = (data: any): string[] => {
	if (!data) {
		return [];
	}
	if (typeof data !== "object") {
		return [String(data)];
	}
	return Object.entries(data).map(([key, val]) => {
		const displayVal = typeof val === "object" && val !== null ? JSON.stringify(val) : String(val);
		return `${key}: ${displayVal}`;
	});
};

export const mapBackendAuditLog = (item: BackendAuditLogItem): AuditLog => {
	return {
		id: item.id,
		time: dayjs(item.createdAt).format("YYYY-MM-DD HH:mm"),
		user: item.userName || "System",
		action: item.action,
		object: `${item.entityType} #${item.entityId.substring(0, 8).toUpperCase()}`,
		ip: item.ipAddress || "-",
		tone: getAuditTone(item.action),
		oldData: formatDataForDiff(item.oldData),
		newData: formatDataForDiff(item.newData),
	};
};

export const auditApi = {
	async getAuditLogs(
		params: AuditLogQueryParams = {}
	): Promise<{ items: AuditLog[]; total: number }> {
		const response = await apiClient.get<AuditLogListResponse>(endpoints.admin.audit, {
			params,
		});

		return {
			items: response.data.data.items.map(mapBackendAuditLog),
			total: response.data.data.meta.total,
		};
	},
};
